import { requestAvatarSelection } from "../../utils/avatar-selection-events";
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Sidebar } from "../sidebar/Sidebar";
import { MediaGrid } from "./MediaGrid";
import { MediaTile } from "./MediaTiles";
import { RenameAvatarDialog } from "./RenameAvatarDialog";
import { DeleteAvatarDialog } from "./DeleteAvatarDialog";
import { useManageAvatars } from "./hooks/useManageAvatars";
import {
  SearchBox,
  Button,
  makeStyles,
  TabList,
  Tab,
  Spinner,
  Body1,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  tokens,
  mergeClasses
} from "@fluentui/react-components";
import {
  Accessibility20Filled,
  Add20Filled,
  ArrowLeft24Filled,
  ArrowRight24Filled,
  ArrowUpload20Filled,
  ChevronDown12Regular,
  ChevronDown24Regular
} from "@fluentui/react-icons";
import { FormattedMessage, useIntl } from "react-intl";
import { avatarThumbnailUrlFor } from "../../utils/media-url-utils";
import useBreakpoints from "../hooks/useBreakpoints";
import { avatarIdentityMatches, replacementToMediaEntry } from "../../utils/avatar-deletion";

const useStyles = makeStyles({
  sidebarContainer: {
    "&.fui-Drawer": {
      height: "0",
      opacity: 0,
      width: "100dvw",
      borderTopRightRadius: tokens.borderRadiusMedium,
      borderTopLeftRadius: tokens.borderRadiusMedium,
      transition: "height 0.2s ease-in-out, opacity 0.2s ease-in-out",
      "@media (min-width: 992px)": {
        width: "500px",
        minWidth: "500px",
        height: "100%",
        opacity: 1
      }
    }
  },
  sidebarContainerOpen: {
    "&.fui-Drawer": {
      height: "310px",
      opacity: 1
    }
  },
  contentContainer: {
    padding: "10px 20px",
    "@media (min-width: 992px)": {
      padding: "24px"
    }
  },
  mediaGrid: {
    marginBottom: tokens.spacingVerticalXXL
  },
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    gap: "10px",
    "@media (min-width: 992px)": {
      gap: "16px"
    }
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  searchAndFilter: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "nowrap",
    "@media (min-width: 992px)": {
      gap: "16px"
    }
  },
  searchInput: {
    flexGrow: 1,
    minWidth: "200px"
  },
  gridContainer: {
    flexGrow: 1,
    position: "relative",
    marginBottom: tokens.spacingVerticalM
  },
  pager: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: "16px"
  },
  noResults: {
    textAlign: "center",
    marginTop: "20px"
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.colorNeutralBackgroundAlpha,
    zIndex: 10
  },
  header: {
    display: "none"
  },
  desktopOnly: {
    display: "none",
    "@media (min-width: 576px)": {
      display: "initial"
    }
  },
  mobileOnly: {
    display: "initial",
    "@media (min-width: 576px)": {
      display: "none"
    }
  },
  mobileCloseButton: {
    position: "absolute",
    bottom: "0",
    left: "20px",
    width: "45px",
    height: "45px",
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderTopLeftRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    transition: "bottom 0.2s ease-in-out",
    "& svg": {
      transition: "transform 0.2s",
      transform: "rotate(180deg)"
    },
    "@media (min-width: 992px)": {
      display: "none"
    }
  },
  mobileCloseButtonOpen: {
    bottom: "310px",
    "& svg": {
      transform: "rotate(0)"
    }
  }
});

const AVATAR_FILTERS = [
  { key: "my-avatars", textId: "media-browser.filter.avatars.my-avatars", defaultText: "My Avatars" },
  { key: "featured", textId: "media-browser.filter.avatars.system-avatars", defaultText: "System Avatars" }
];

export function AvatarManagementSidebar({
  onAvatarSelected,
  onSpawnAvatar,
  onCreateAvatar,
  onAvatarUpload,
  isSignedIn,
  initialAvatarId
}) {
  const intl = useIntl();
  const styles = useStyles();
  const {
    avatars,
    loading,
    error,
    query,
    setQuery,
    filter,
    changeFilter,
    hasNextPage,
    hasPrevPage,
    goToNextPage,
    goToPrevPage,
    renameAvatarAPI,
    deleteAvatarAPI,
    remixAvatarAPI
  } = useManageAvatars(isSignedIn, isSignedIn ? "my-avatars" : "featured");

  const [selectedAvatarForAction, setSelectedAvatarForAction] = useState(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentSelectedAvatarId, setCurrentSelectedAvatarId] = useState(initialAvatarId);
  const [showGlbInput, setShowGlbInput] = useState(false);
  const glbInputRef = useRef();
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    setCurrentSelectedAvatarId(initialAvatarId);
  }, [initialAvatarId]);

  const handleSelectAvatar = entry => {
    setCurrentSelectedAvatarId(entry.id);
    if (onAvatarSelected) {
      onAvatarSelected(entry, "use");
    }
  };

  const handleRename = entry => {
    setSelectedAvatarForAction(entry);
    setShowRenameDialog(true);
  };

  const handleDelete = entry => {
    setSelectedAvatarForAction(entry);
    setShowDeleteDialog(true);
  };

  const handleRemix = entry => {
    const newName = `${entry.name || "Avatar"}`;
    remixAvatarAPI(entry.id, newName);
  };

  const handleSpawnAvatar = (e, entry) => {
    e.preventDefault();
    e.stopPropagation();

    onSpawnAvatar(entry);
  };

  const processAvatarThumbnailUrl = (entry, thumbnailWidth, thumbnailHeight) => {
    if (!entry.images?.preview?.url) return "";
    return avatarThumbnailUrlFor(entry.images.preview.url, thumbnailWidth, thumbnailHeight);
  };

  const handleCustomAvatarUpload = () => {
    setShowGlbInput(true);
    setTimeout(() => {
      if (glbInputRef.current) glbInputRef.current.click();
    }, 0);
  };

  // Handle file input change
  const handleGlbFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    // Convert file to blob URL
    const url = URL.createObjectURL(file);
    // Pass the url to AvatarEditor by calling onAvatarSelected with a special object
    if (onAvatarUpload) {
      onAvatarUpload({
        id: undefined,
        gltfUrl: url,
        name: file.name || "Custom Avatar"
      });
    }
    setShowGlbInput(false);
    // Optionally, revokeObjectURL after AvatarEditor is done
  };

  const handleCreateAvatar = () => {
    onCreateAvatar();
  };

  const breakpoint = useBreakpoints();
  const isMobile = ["xs", "sm", "md"].includes(breakpoint);

  const visibleAvatarFilters = isSignedIn ? AVATAR_FILTERS : AVATAR_FILTERS.filter(f => f.key !== "my-avatars");

  return (
    <>
      {/* Hidden GLB file input for custom avatar upload */}
      {showGlbInput && (
        <input
          ref={glbInputRef}
          type="file"
          accept="model/gltf-binary,.glb,model/gltf+binary"
          style={{ display: "none" }}
          onChange={handleGlbFileChange}
        />
      )}
      <div
        role="button"
        className={mergeClasses(styles.mobileCloseButton, isOpen && styles.mobileCloseButtonOpen)}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={intl.formatMessage({ id: "sidebar.close", defaultMessage: "Close component" })}
      >
        <ChevronDown24Regular />
      </div>
      <Sidebar
        headerClassName={styles.header}
        className={mergeClasses(styles.sidebarContainer, isOpen && styles.sidebarContainerOpen)}
        position={isMobile ? "bottom" : "end"}
        type={isMobile ? "overlay" : "inline"}
        contentClassName={styles.contentContainer}
      >
        <div className={styles.container}>
          <div className={styles.controls}>
            <TabList selectedValue={filter} onTabSelect={(_, data) => changeFilter(data.value)}>
              {visibleAvatarFilters.map(f => (
                <Tab key={f.key} value={f.key}>
                  {intl.formatMessage({ id: f.textId, defaultMessage: f.defaultText })}
                </Tab>
              ))}
            </TabList>
            <div className={styles.searchAndFilter}>
              <SearchBox
                className={styles.searchInput}
                value={query}
                onChange={(_, data) => setQuery(data.value)}
                placeholder={intl.formatMessage({
                  id: "avatar-management-sidebar.search-placeholder",
                  defaultMessage: "Search"
                })}
                aria-label={intl.formatMessage({
                  id: "avatar-management-sidebar.search-placeholder",
                  defaultMessage: "Search"
                })}
                appearance="filled-darker"
              />
              <Menu>
                <MenuTrigger disabled={loading} disableButtonEnhancement>
                  {filter === "my-avatars" && (
                    <Button disabled={loading} appearance="primary" icon={<Add20Filled />}>
                      <span className={styles.desktopOnly}>
                        <FormattedMessage id="more-menu.avatar" defaultMessage="Add Avatar" />
                      </span>
                      <span className={styles.mobileOnly}>
                        <FormattedMessage id="more-menu.avatar-mobile" defaultMessage="Add" />
                      </span>
                      &nbsp;
                      <ChevronDown12Regular />
                    </Button>
                  )}
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuGroup>
                      <MenuGroupHeader>
                        <FormattedMessage id="avatar-editor.create" defaultMessage="Create" />
                      </MenuGroupHeader>
                      <MenuItem disabled={loading} icon={<Accessibility20Filled />} onClick={handleCreateAvatar}>
                        <FormattedMessage id="avatar-editor.title" defaultMessage="Avatar" />
                      </MenuItem>
                    </MenuGroup>
                    <MenuGroup>
                      <MenuGroupHeader>
                        <FormattedMessage id="place-popover.item-type.upload" defaultMessage="Upload" />
                      </MenuGroupHeader>
                      <MenuItem disabled={loading} icon={<ArrowUpload20Filled />} onClick={handleCustomAvatarUpload}>
                        <FormattedMessage id="avatar-editor.create-custom-avatar" defaultMessage="Custom Avatar" />
                      </MenuItem>
                    </MenuGroup>
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          </div>

          <div className={styles.gridContainer}>
            {loading && (
              <div className={styles.loadingOverlay}>
                <Spinner
                  label={intl.formatMessage({
                    id: "avatar-management-sidebar.loading",
                    defaultMessage: "Loading avatars..."
                  })}
                />
              </div>
            )}
            {error && <Body1 style={{ color: "red", textAlign: "center" }}>{error}</Body1>}

            {!loading && !error && avatars.length === 0 && (
              <Body1 className={styles.noResults}>
                {intl.formatMessage({
                  id: "avatar-management-sidebar.no-avatars",
                  defaultMessage: "No avatars found."
                })}
              </Body1>
            )}

            {!loading && !error && avatars.length > 0 && (
              <MediaGrid className={styles.mediaGrid}>
                {avatars.map(avatar => (
                  <MediaTile
                    key={avatar.id || avatar.avatar_id} // Use avatar_id as fallback if id is missing
                    entry={{ ...avatar, id: avatar.id || avatar.avatar_id }} // Ensure entry has an 'id'
                    processThumbnailUrl={processAvatarThumbnailUrl}
                    onClick={e => {
                      e.preventDefault();
                      handleSelectAvatar(avatar);
                    }}
                    onEdit={() => handleRename(avatar)}
                    onDelete={() => handleDelete(avatar)}
                    onCopy={isSignedIn ? () => handleRemix(avatar) : undefined}
                    onSpawn={onSpawnAvatar ? e => handleSpawnAvatar(e, avatar) : undefined}
                    avatarSelected={{ id: currentSelectedAvatarId }}
                  />
                ))}
              </MediaGrid>
            )}
          </div>

          {!loading && avatars.length > 0 && (hasNextPage || hasPrevPage) && (
            <div className={styles.pager}>
              <Button
                icon={<ArrowLeft24Filled />}
                onClick={goToPrevPage}
                disabled={!hasPrevPage}
                appearance="transparent"
                aria-label={intl.formatMessage({ id: "pager.previous", defaultMessage: "Previous page" })}
              />
              <Button
                icon={<ArrowRight24Filled />}
                onClick={goToNextPage}
                disabled={!hasNextPage}
                appearance="transparent"
                aria-label={intl.formatMessage({ id: "pager.next", defaultMessage: "Next page" })}
              />
            </div>
          )}

          {selectedAvatarForAction && (
            <>
              <RenameAvatarDialog
                avatarId={selectedAvatarForAction.id || selectedAvatarForAction.avatar_id}
                currentName={selectedAvatarForAction.name}
                open={showRenameDialog}
                onOpenChange={(_, data) => {
                  setShowRenameDialog(data.open);
                  if (!data.open) setSelectedAvatarForAction(null);
                }}
                onSave={updatedAvatarData => {
                  // renameAvatarAPI expects (avatarId, newName)
                  renameAvatarAPI(updatedAvatarData.avatar_id, updatedAvatarData.name);
                  setShowRenameDialog(false);
                  setSelectedAvatarForAction(null);
                }}
              />
              <DeleteAvatarDialog
                avatar={selectedAvatarForAction}
                open={showDeleteDialog}
                onOpenChange={(_, data) => {
                  setShowDeleteDialog(data.open);
                  if (!data.open) setSelectedAvatarForAction(null);
                }}
                onSave={async () => {
                  const deletedId = selectedAvatarForAction.id || selectedAvatarForAction.avatar_id;
                  const result = await deleteAvatarAPI(deletedId);
                  if (avatarIdentityMatches(currentSelectedAvatarId, result.deleted)) {
                    const replacement = replacementToMediaEntry(result.replacement);
                    setCurrentSelectedAvatarId(replacement?.id ?? null);
                    requestAvatarSelection(window.APP.scene, replacement?.id ?? null, { source: "repair" });
                  }
                  setShowDeleteDialog(false);
                  setSelectedAvatarForAction(null);
                }}
              />
            </>
          )}
        </div>
      </Sidebar>
    </>
  );
}

AvatarManagementSidebar.propTypes = {
  onAvatarSelected: PropTypes.func.isRequired,
  onCreateAvatar: PropTypes.func.isRequired,
  onAvatarUpload: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  hubChannel: PropTypes.object,
  initialAvatarId: PropTypes.string,
  showNonHistoriedDialog: PropTypes.func,
  onSpawnAvatar: PropTypes.func,
  isSignedIn: PropTypes.bool
};
